import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { ProgressBar, Spinner, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { formatMissionDate } from "../../Utils/MeetingFunctions";
import { useTranslation } from "react-i18next";

const MissionRoadmap = ({
  isLoading = false,
  progress = 0,
  data = [],
  startDate,
  endDate,
  onPrevious,
  onNext,
  onReset,
}) => {
  const [t] = useTranslation("global")
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setCurrentDate(new Date()), 86400000);
    return () => clearInterval(timer);
  }, []);

  const parseDate = (dateString) => {
    if (!dateString) return new Date();
    const [day, month, year] = dateString.split("/");
    return new Date(year, month - 1, day);
  };

  // const currentDatePosition = useMemo(() => {
  //   const chartStart = new Date(startDate);
  //   const chartEnd = new Date(endDate);
  //   const totalDuration = chartEnd - chartStart;
  //   const elapsed = currentDate - chartStart;
  //   return (elapsed / totalDuration) * 100;
  // }, [currentDate, startDate, endDate]);
   const currentDatePosition = useMemo(() => {
     const chartStart = new Date(startDate);
      const chartEnd = new Date(endDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
  
      if (today <= chartStart) return 0;
      if (today >= chartEnd) return 100;
  
      const totalDuration = chartEnd - chartStart;
      const elapsed = today - chartStart;
      return parseFloat(((elapsed / totalDuration) * 100).toFixed(2));
    }, [startDate, endDate]);

  const { monthHeaders, monthDividers } = useMemo(() => {
    const headers = [];
    const dividers = [];
    const current = new Date(startDate);
    const end = new Date(endDate);

    headers.push(new Date(current));

    const clientColumnWidth = 200;
    const totalWidth = window.innerWidth * 0.9;
    const clientColumnPercent = (clientColumnWidth / totalWidth) * 100;

    for (let i = 0; i < 7; i++) {
      const nextMonth = new Date(current);
      nextMonth.setMonth(nextMonth.getMonth() + 1);

      if (nextMonth > end) break;

      headers.push(new Date(nextMonth));

      const daysIntoPeriod = (nextMonth - startDate) / (1000 * 60 * 60 * 24);
      const totalDays = (endDate - startDate) / (1000 * 60 * 60 * 24);
      const dividerPos =
        clientColumnPercent +
        (daysIntoPeriod / totalDays) * (100 - clientColumnPercent);

      dividers.push(dividerPos);
      current.setMonth(current.getMonth() + 1);
    }

    return { monthHeaders: headers, monthDividers: dividers };
  }, [startDate, endDate]);

  const displayMissions = useMemo(() => {
    if (!Array.isArray(data)) return [];

    return data.flatMap((client) =>
      client.destinations
        .filter((destination) => {
          if (!destination.meeting_start_date || !destination.meeting_end_date) {
            return false;
          }

          const start = parseDate(
            formatMissionDate(destination.meeting_start_date)
          );
          const end = parseDate(
            formatMissionDate(destination.meeting_end_date)
          );

          if (!start || !end) return false;

          return (
            (start >= startDate && start <= endDate) ||
            (end >= startDate && end <= endDate) ||
            (start <= startDate && end >= endDate)
          );
        })
        .map((destination) => ({
          id: destination.id,
          clients: [{ id: client.id, name: client.name }],
          destination_name: destination.destination_name,
          meeting_start_date: formatMissionDate(destination.meeting_start_date),
          meeting_end_date: formatMissionDate(destination.meeting_end_date),
          total_meetings_count: destination.total_meetings || 0,
          completed_meetings_count: destination.completed_meetings || 0,
          total_steps: destination.total_steps || 0,
          completed_steps: destination.completed_steps || 0,
          status: destination.status,
          delay: destination.delay,
          budget_exceeded: destination.budget_exceeded,
          meetings: Array(destination.total_meetings || 0).fill({
            status:
              destination.completed_meetings >= destination.total_meetings
                ? "completed"
                : "active",
          }),
        }))
    );
  }, [data, startDate, endDate]);

  const displayClients = useMemo(() => {
    if (!Array.isArray(data)) return [];

    return data
      .map((client) => ({
        id: client.id,
        name: client.name,
        avatar: client.client_logo,
      }))
      .filter((client) =>
        displayMissions.some((mission) => mission.clients[0]?.id === client.id)
      );
  }, [data, displayMissions]);

  const getMissionStatusColor = (mission) => {
    if (mission?.status === "closed") return "rgb(119, 214, 113)";
     if (
    mission?.delay === true ||
    mission?.budget_exceeded === true ||
    (mission?.status === "in_progress" &&
      (mission?.delay || mission?.budget_exceeded))
  ) {
    return "red";
  }

  if (
    mission?.status === "in_progress" &&
    mission?.delay === false &&
    mission?.budget_exceeded === false
  ) {
    return "#f2db43"; // yellow
  }
   
    if (mission?.status === "active") return "rgb(91, 170, 234)";
    return "#4e79a7";
  };

  const calculateMissionPosition = (mission) => {
    const start = parseDate(mission.meeting_start_date);
    const end = parseDate(mission.meeting_end_date);
    const chartStart = new Date(startDate);
    const chartEnd = new Date(endDate);

    const effectiveStart = start < chartStart ? chartStart : start;
    const effectiveEnd = end > chartEnd ? chartEnd : end;

    const totalDuration = chartEnd - chartStart;
    let startPos =
      Math.max(0, (effectiveStart - chartStart) / totalDuration) * 100;
    let endPos =
      Math.min(100, (effectiveEnd - chartStart) / totalDuration) * 100;

    // Calculate width with minimum of 2% or actual width
    let width = endPos - startPos;
    const MIN_WIDTH = 2; // Minimum width in percentage
    if (width < MIN_WIDTH) {
      // Center the bar if it's too small
      const center = (startPos + endPos) / 2;
      startPos = center - MIN_WIDTH / 2;
      endPos = center + MIN_WIDTH / 2;
      width = MIN_WIDTH;
    }

    // const totalMeetings = mission?.total_meetings_count || 0;
    // const completedMeetings = mission?.completed_meetings_count || 0;
     const totalMeetings = parseInt(mission?.total_steps) || 0;
    const completedMeetings = parseInt(mission?.completed_steps) || 0;
    const percentageCompleted =
      totalMeetings > 0
        ? Math.floor((completedMeetings / totalMeetings) * 100)
        : 0;

    return {
      left: `${startPos}%`,
      width: `${width}%`,
      completedWidth: `${percentageCompleted}%`,
      percentage: percentageCompleted,
      color: getMissionStatusColor(mission),
    };
  };

      const statuses = [
        // { name: t("mission-badge.new"), color: "rgb(91 170 234)" },
        { name: t("mission-badges.upcoming"), color: "rgb(91 170 234)" },
        { name: t("mission-badges.inProgress"), color: "yellow" },
        { name: t("mission-badges.alert"), color: "red" },
      { name: t("mission-badges.completed"), color: "rgb(119 214 113)" },
      // { name: t("mission-badge.status3"), color: "red" },
    ];
  
  return (
    <>
      {isLoading ? (
        <div className="progress-overlay" style={{ background: "transparent" }}>
          <div style={{ width: "50%" }}>
            <ProgressBar now={progress} animated />
          </div>
        </div>
      ) : (
        <div className="gantt-container">
          {/* Navigation Controls */}
     <div className="roadmap-navigation" style={{ marginBottom: "10px" }}>
  {/* Mobile layout (stacked) */}
  <div className="d-block d-md-none">
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
      <Button
        variant="outline-secondary"
        onClick={onReset}
        size="sm"
        style={{ marginRight: "8px", flex: 1 }}
      >
        {t("calendar.reset")}
      </Button>
      <Button
        className="btn-nxt-pre"
        onClick={onPrevious}
        size="sm"
        style={{ marginRight: "8px", flex: 1 }}
      >
        {t("calendar.prev")}
      </Button>
      <Button
        className="btn-nxt-pre"
        onClick={onNext}
        size="sm"
        style={{ flex: 1 }}
      >
        {t("calendar.next")}
      </Button>
    </div>
    <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "14px" }}>
      {startDate.toLocaleDateString(t("calendar.locale"), {
        month: "short",
        year: "numeric",
      })}{" "}
      -{" "}
      {endDate.toLocaleDateString(t("calendar.locale"), {
        month: "short",
        year: "numeric",
      })}
    </div>
  </div>

  {/* Desktop layout (original) */}
  <div className="d-none d-md-flex" style={{ justifyContent: "space-between", padding: "0 10px" }}>
    <div>
      <Button
        variant="outline-secondary"
        onClick={onReset}
        style={{ marginRight: "10px" }}
      >
        {t("calendar.reset")}
      </Button>
      <span style={{ fontWeight: "bold" }}>
        {startDate.toLocaleDateString(t("calendar.locale"), {
          month: "long",
          year: "numeric",
        })}{" "}
        -{" "}
        {endDate.toLocaleDateString(t("calendar.locale"), {
          month: "long",
          year: "numeric",
        })}
      </span>
    </div>
    <div>
      <Button
        className="btn-nxt-pre me-2"
        onClick={onPrevious}
      >
        {t("calendar.prev")}
      </Button>
      <Button className="btn-nxt-pre" onClick={onNext}>
        {t("calendar.next")}
      </Button>
    </div>
  </div>
</div>

          <div className="gantt-scroll-wrapper">
            {/* Current date indicator */}
            <div
              className="current-date-line"
              style={{
                left: `calc(200px + (100% - 200px) * ${
                  currentDatePosition / 100
                })`,
              }}
            />

            <div className="gantt-header gantt-row">
              <div className="gantt-cell gantt-client-header">Clients</div>
              {monthHeaders.map((month, index) => (
                <div key={index} className="gantt-cell gantt-month-header">
                  {month.toLocaleString("default", { month: "short" })}{" "}
                  {month.getFullYear().toString().slice(-2)}
                </div>
              ))}
            </div>

            {/* Month divider lines */}
            {monthDividers.map((pos, index) => (
              <div
                key={`divider-${index}`}
                className="month-divider"
                style={{ left: `${pos}%` }}
              />
            ))}

            {/* Client and mission rows */}
            {displayClients.map((client) => {
              const clientMissions = displayMissions.filter(
                (mission) => mission.clients[0]?.id === client.id
              );

              const missionGroups = [];
              clientMissions.forEach((mission) => {
                const position = calculateMissionPosition(mission);
                let placed = false;

                // for (let group of missionGroups) {
                //   const overlaps = group.some((existingMission) => {
                //     const existingPos =
                //       calculateMissionPosition(existingMission);
                //     return !(
                //       parseFloat(position.left) >=
                //         parseFloat(existingPos.left) +
                //           parseFloat(existingPos.width) ||
                //       parseFloat(position.left) + parseFloat(position.width) <=
                //         parseFloat(existingPos.left)
                //     );
                //   });

                //    if (!overlaps) {
                //     group.push(mission);
                //     placed = true;
                //     break;
                //   }
                // }

                if (!placed) {
                  missionGroups.push([mission]);
                }
              });

              return (
                <div key={client.id} className="gantt-row">
                  <div className="gantt-cell gantt-client-name">
                    <div className="client-avatar-container">
                      {client.avatar ? (
                        <img
                          src={client.avatar}
                          alt={client.name}
                          onClick={() => navigate(`/client/${client?.id}`)}
                          style={{
                            cursor: "pointer",
                            objectFit: "cover",
                            width: "30px",
                            height: "30px",
                            objectPosition: "top",
                            borderRadius: "50%",
                          }}
                          // onError={(e) => {
                          //   e.target.onerror = null;
                          //   e.target.src = "https://via.placeholder.com/100";
                          // }}
                        />
                      ) : (
                        <div className="client-avatar-placeholder" />
                      )}
                      <span>{client.name}</span>
                    </div>
                  </div>
                  <div
                    className="gantt-cell gantt-mission-track"
                    style={{ minHeight: `${missionGroups.length * 30 + 10}px` }}
                  >
                    {missionGroups.map((group, groupIndex) => (
                      <div
                        key={`group-${groupIndex}`}
                        className="mission-group"
                      >
                        {group.map((mission, missionIndex) => {
                          const position = calculateMissionPosition(mission);
                          return (
                            <div
                              key={mission.id}
                              className="gantt-mission-container"
                              style={{
                                left: position.left,
                                width: position.width,
                                top: `${missionIndex * 30}px`,
                                zIndex: missionIndex + 1,
                              }}
                              onClick={() =>
                                navigate(`/invitiesToMeeting/${mission.id}`)
                              }
                            >
                              <div
                                className="gantt-mission-bar"
                                style={{
                                  width: "100%",
                                  backgroundColor: position.color,
                                  opacity: 0.3,
                                }}
                              />
                              <div
                                className="gantt-mission-completed"
                               style={{
                                  width: position.completedWidth,
                                  backgroundColor: position.color,
                                }}
                              />
                              <div
                                className="gantt-mission-label"
                                title={`${mission.destination_name} ${
                                  mission?.meeting_start_date ||
                                  mission?.meeting_end_date
                                    ? `(${formatMissionDate(
                                        mission?.meeting_start_date
                                      )} to ${formatMissionDate(
                                        mission?.meeting_end_date
                                      )})`
                                    : ""
                                } - ${position.percentage}% completed`}
                              >
                                {mission.destination_name} (
                                {position.percentage}%)
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

           <div className="status-legend">
             <div className="status-list">
               {statuses.map((status, index) => (
                 <div key={index} className="status-item">
                   <span
                     className="status-dot"
                     style={{ backgroundColor: status.color }}
                   ></span>
                   {status.name}
                 </div>
               ))}
             </div>
           </div>
        </div>
      )}
    </>
  );
};

export default MissionRoadmap;
